/**
 * Hook de Monitoramento da DLQ de Contratos
 * 
 * Fornece funções para buscar estatísticas, listar itens,
 * reprocessar e descartar mensagens da DLQ de contratos.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

/** Estatísticas da DLQ de contratos */
export interface DlqContractsStats {
  total: number;
  byReason: Record<string, number>;
  oldestAt: string | null;
  newestAt: string | null;
}

/** Item da DLQ de contratos */
export interface DlqContractsItem {
  msg_id: number;
  enqueued_at: string;
  message: Record<string, unknown>;
  dlq_reason: string;
  dlq_error: string | null;
  dlq_moved_at: string;
  original_msg_id: number | null;
  tenant_id: string | null;
  contract_id: string | null;
  action: string | null;
  // Campos enriquecidos
  tenant_name?: string;
  contract_title?: string;
}

// ============================================================================
// INTERFACES DE RESPOSTA DAS RPCS
// ============================================================================

interface DlqStatsRpcResponse {
  total_count: number;
  by_reason: Record<string, number>;
  oldest_message_at: string | null;
  newest_message_at: string | null;
}

interface DlqItemRpcResponse {
  msg_id: number;
  enqueued_at: string;
  message: Record<string, unknown>;
  dlq_reason: string;
  dlq_error: string | null;
  dlq_moved_at: string;
  original_msg_id: number | null;
  tenant_id: string | null;
  contract_id: string | null;
  action: string | null;
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook para buscar estatísticas da DLQ de contratos
 */
export function useDlqContractsStats() {
  return useQuery({
    queryKey: ['dlq-contracts-stats'],
    queryFn: async (): Promise<DlqContractsStats> => {
      const { data, error } = await supabase.rpc('get_dlq_contracts_stats');
      if (error) {
        logger.error('[useDlqContractsStats] Erro ao buscar estatísticas:', error.message);
        throw error;
      }

      const stats = (data as unknown as DlqStatsRpcResponse[])?.[0];
      if (!stats) return { total: 0, byReason: {}, oldestAt: null, newestAt: null };

      return {
        total: stats.total_count || 0,
        byReason: stats.by_reason || {},
        oldestAt: stats.oldest_message_at,
        newestAt: stats.newest_message_at
      };
    },
    refetchInterval: 30000,
  });
}

/**
 * Hook para buscar itens da DLQ de contratos com paginação e filtro
 */
export function useDlqContractsItems(
  limit: number = 50,
  offset: number = 0,
  reasonFilter: string | null = null
) {
  return useQuery({
    queryKey: ['dlq-contracts-items', limit, offset, reasonFilter],
    queryFn: async (): Promise<DlqContractsItem[]> => {
      const { data, error } = await supabase.rpc('get_dlq_contracts_items', {
        p_limit: limit,
        p_offset: offset,
        p_reason: reasonFilter
      });

      if (error) {
        logger.error('[useDlqContractsItems] Erro ao buscar itens:', error.message);
        throw error;
      }

      if (!data || data.length === 0) return [];

      const rawItems = data as unknown as DlqItemRpcResponse[];

      // Enriquecimento de tenant names
      const tenantIds = new Set<string>();
      rawItems.forEach(item => { if (item.tenant_id) tenantIds.add(item.tenant_id); });

      let tenantMap = new Map<string, string>();
      if (tenantIds.size > 0) {
        const { data: tenants } = await supabase
          .from('tenants').select('id, name').in('id', Array.from(tenantIds));
        tenantMap = new Map((tenants || []).map((t: { id: string; name: string }) => [t.id, t.name]));
      }

      return rawItems.map(item => ({
        ...item,
        tenant_name: item.tenant_id ? tenantMap.get(item.tenant_id) || 'N/A' : 'N/A',
        contract_title: (item.message?.contract_title as string) || (item.message?.title as string) || '',
      }));
    },
    refetchInterval: 30000,
  });
}

/**
 * Hook para reprocessar um item da DLQ de contratos
 */
export function useReprocessDlqContractsItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (msgId: number): Promise<boolean> => {
      const { data, error } = await supabase.rpc('reprocess_dlq_contracts_item', { p_msg_id: msgId });
      if (error) {
        logger.error('[useReprocessDlqContractsItem] Erro ao reprocessar:', error.message);
        throw error;
      }
      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dlq-contracts-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-contracts-items'] });
      queryClient.invalidateQueries({ queryKey: ['contract-queue-stats'] });
    }
  });
}

/**
 * Hook para descartar um item da DLQ de contratos
 */
export function useDiscardDlqContractsItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (msgId: number): Promise<boolean> => {
      const { data, error } = await supabase.rpc('discard_dlq_contracts_item', { p_msg_id: msgId });
      if (error) {
        logger.error('[useDiscardDlqContractsItem] Erro ao descartar:', error.message);
        throw error;
      }
      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dlq-contracts-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-contracts-items'] });
    }
  });
}
